import React, { useEffect } from 'react';
import { connect } from '../../../../redux/store';
import { StaticTypeahead, UserTypeahead, C } from '@kineticdata/bundle-common';
import { I18n } from '@kineticdata/react';
import { DefaultActions } from './';
import { Markdown } from '../Markdown';
import { CONTENT_MANAGER_MODE } from '../helpers';
import { connectUser } from '../../../../utils';
import { List } from 'immutable';

const UserDisplay = connectUser(
  ({ user, username }) => (
    <li>
      {user === false ? (
        <p className="text-danger">
          <span className="fa fa-exclamation-triangle fa-fw" />
          <I18n>User not found</I18n>: {username}
        </p>
      ) : !user ? (
        <p className="text-muted">
          <span className="fa fa-spinner fa-spin fa-fw" />
          <I18n>Loading</I18n>
        </p>
      ) : (
        <p>
          <span className="">{user.displayName}</span> {'<'}
          <a href={`mailto:${user.email}`}>{user.email}</a>
          {'>'}
        </p>
      )}
    </li>
  ),
  props => props.username,
);

const TeamDisplay = ({ team }) => (
  <li>
    <p>
      <strong>{team.name}</strong>
    </p>
    {team.memberships &&
      team.memberships.length > 0 && (
        <ul>
          {team.memberships.map(({ user }) => (
            <li key={user.username}>
              <span className="">{user.displayName}</span> {'<'}
              <a href={`mailto:${user.email}`}>{user.email}</a>
              {'>'}
            </li>
          ))}
        </ul>
      )}
  </li>
);

const UserSelectionComponent = connectUser(
  ({ selection, remove, user, fetchUsers }) => {
    // Fetch the user record
    useEffect(
      () => {
        if (typeof user === 'undefined') {
          fetchUsers({ usernames: [selection.get('username')] });
        }
      },
      [fetchUsers, user, selection],
    );
    return (
      <div className="input-group selection">
        <input
          className="form-control"
          type="text"
          disabled
          value={`${
            user
              ? user.displayName || user.email
              : user === false
                ? 'User Not Found'
                : 'Loading...'
          } (${selection.get('username')})`}
        />
        <div className="input-group-append">
          <button
            className="btn btn-sm btn-clear"
            onClick={remove}
            type="button"
          >
            <span className="sr-only">Remove</span>
            <i className="fa fa-fw fa-times" />
          </button>
        </div>
      </div>
    );
  },
  props => props.selection.get('username'),
);

const TeamSuggestionContent = ({ suggestion }) => {
  return (
    <>
      {suggestion.get('parentName') && (
        <div className="small">{suggestion.get('parentName')}</div>
      )}
      <div className="large">{suggestion.get('localName')}</div>
    </>
  );
};

const mapTeamToTeamOptions = team => {
  const [localName, ...parents] = team.name.split('::').reverse();
  return {
    localName,
    name: team.name,
    parentName: parents.reverse().join('::'),
  };
};

const enhance = connect((state, props) => {
  const isPublicMode = props.mode === CONTENT_MANAGER_MODE.PUBLIC;
  const isViewMode = props.mode !== CONTENT_MANAGER_MODE.EDIT;
  const id = props.activeItem && props.activeItem.id;
  const technicalOwnerTeams = state.app.subteamsMap.get(
    C.CATALOG_TECHNICAL_OWNERS_TEAM_NAME,
    List(),
  );
  const businessOwnerTeams = state.app.subteamsMap.get(
    C.CATALOG_BUSINESS_OWNERS_TEAM_NAME,
    List(),
  );

  return {
    isPublicMode,
    isViewMode,
    id,
    technicalOwnerTeams,
    businessOwnerTeams,
    technicalOwnerTeamOptions: technicalOwnerTeams.map(mapTeamToTeamOptions),
    businessOwnerTeamOptions: businessOwnerTeams.map(mapTeamToTeamOptions),
    fetchSubteams: state.app.actions.fetchSubteams,
    usersMap: state.app.usersMap,
    fetchUsers: state.app.actions.fetchUsers,
  };
});

const ServiceManagementRenderer = enhance(
  ({
    mode,
    isPublicMode,
    isViewMode,
    content,
    values,
    activeItem,
    id,
    fetchSubteams,
    ...props
  }) => {
    // Fetch the teams lists
    useEffect(
      () => {
        fetchSubteams({ teamName: C.CATALOG_TECHNICAL_OWNERS_TEAM_NAME });
        fetchSubteams({ teamName: C.CATALOG_BUSINESS_OWNERS_TEAM_NAME });
      },
      [fetchSubteams],
    );

    return !!activeItem ? (
      <>
        <h2>
          <I18n>{activeItem.title}</I18n>
        </h2>

        <div className="form">
          {!isViewMode && (
            <div className="form-group">
              <label htmlFor="title">
                <I18n>Section Title</I18n>
              </label>
              <input
                type="text"
                id="title"
                value={activeItem.title}
                onChange={props.handleContentChange(id, 'title')}
              />
            </div>
          )}

          <div className="toastui-editor-contents">
            <h3>Service Owners</h3>
            <ul>
              {(values.get('Service Owners') || []).map(username => (
                <UserDisplay key={username} username={username} />
              ))}
              <li className="empty-list">
                <em>
                  <I18n>None</I18n>
                </em>
              </li>
            </ul>
          </div>

          {!isViewMode && (
            <div className="form-group">
              <label htmlFor="service-owners" className="field-label">
                <I18n>Add/Remove Service Owners</I18n>
              </label>
              <UserTypeahead
                labelProp="displayName"
                value={(values.get('Service Owners') || []).map(u => ({
                  username: u,
                }))}
                onChange={users =>
                  props.handleValuesChangeDirect(id, 'Service Owners')(
                    users.toJS().map(u => u.username),
                  )
                }
                search={{ limit: 25 }}
                components={{
                  Selection: UserSelectionComponent,
                  Input: isViewMode
                    ? (values.get('Service Owners') || []).length > 0
                      ? () => null
                      : () => (
                          <div className="form-control-plaintext">
                            <em>None</em>
                          </div>
                        )
                    : undefined,
                }}
                multiple={true}
                minSearchLength={1}
                id="service-owners"
                disabled={isViewMode}
              />
              <small className="d-flex p-1">
                <I18n>The users who own this service.</I18n>
              </small>
            </div>
          )}

          <div className="toastui-editor-contents">
            <h3>Technical Owners</h3>
            <ul>
              {(values.get('Technical Owners') || [])
                .map(teamName =>
                  props.technicalOwnerTeams.find(t => t.name === teamName),
                )
                .filter(Boolean)
                .map(team => <TeamDisplay key={team.slug} team={team} />)}
              <li className="empty-list">
                <em>
                  <I18n>None</I18n>
                </em>
              </li>
            </ul>
          </div>

          {!isViewMode && (
            <div className="form-group">
              <label htmlFor="service-tech-owners" className="field-label">
                <I18n>Add/Remove Technical Owners</I18n>
              </label>
              <StaticTypeahead
                options={props.technicalOwnerTeamOptions.toJS()}
                value={(values.get('Technical Owners') || []).map(t => ({
                  name: t,
                }))}
                onChange={teams =>
                  props.handleValuesChangeDirect(id, 'Technical Owners')(
                    teams.toJS().map(t => t.name),
                  )
                }
                search={{
                  fields: [
                    { name: 'localName', operator: 'matches' },
                    { name: 'parentName', operator: 'matches' },
                  ],
                }}
                valueProp="name"
                components={{
                  SuggestionContent: TeamSuggestionContent,
                  Input: isViewMode
                    ? (values.get('Technical Owners') || []).length > 0
                      ? () => null
                      : () => (
                          <div className="form-control-plaintext">
                            <em>None</em>
                          </div>
                        )
                    : undefined,
                }}
                multiple={true}
                minSearchLength={0}
                id="service-tech-owners"
                disabled={isViewMode}
              />
              <small className="d-flex p-1">
                <I18n>
                  The teams whose members have access to revise this service.
                </I18n>
              </small>
            </div>
          )}

          <div className="toastui-editor-contents">
            <h3>Business Owners</h3>
            <ul>
              {(values.get('Business Owners') || [])
                .map(teamName =>
                  props.businessOwnerTeams.find(t => t.name === teamName),
                )
                .filter(Boolean)
                .map(team => <TeamDisplay key={team.slug} team={team} />)}
              <li className="empty-list">
                <em>
                  <I18n>None</I18n>
                </em>
              </li>
            </ul>
          </div>

          {!isViewMode && (
            <div className="form-group">
              <label htmlFor="service-bus-owners" className="field-label">
                <I18n>Add/Remove Business Owners</I18n>
              </label>
              <StaticTypeahead
                options={props.businessOwnerTeamOptions.toJS()}
                value={(values.get('Business Owners') || []).map(t => ({
                  name: t,
                }))}
                onChange={teams =>
                  props.handleValuesChangeDirect(id, 'Business Owners')(
                    teams.toJS().map(t => t.name),
                  )
                }
                search={{
                  fields: [
                    { name: 'localName', operator: 'matches' },
                    { name: 'parentName', operator: 'matches' },
                  ],
                }}
                valueProp="name"
                components={{
                  SuggestionContent: TeamSuggestionContent,
                  Input: isViewMode
                    ? (values.get('Business Owners') || []).length > 0
                      ? () => null
                      : () => (
                          <div className="form-control-plaintext">
                            <em>None</em>
                          </div>
                        )
                    : undefined,
                }}
                multiple={true}
                minSearchLength={0}
                id="service-bus-owners"
                disabled={isViewMode}
              />
            </div>
          )}

          {!isViewMode ? (
            <>
              <div className="form-group">
                <label htmlFor="content">
                  <I18n>Additional Section Content</I18n>
                </label>
                <Markdown
                  initialValue={activeItem.value}
                  onChange={props.handleContentChangeDirect(id, 'value')}
                  imageDir={props.imageDir}
                />
                <small>
                  <I18n>
                    Use Heading 3 to match the headings of the predefined owner
                    groups.
                  </I18n>
                </small>
              </div>

              <DefaultActions
                id={id}
                isDirty={activeItem.isDirty}
                handleResetSection={props.handleResetSection}
                handleContentDelete={props.handleContentDelete}
                actionsPending={props.actionsPending}
              />
            </>
          ) : (
            <Markdown initialValue={activeItem.value} disabled={isViewMode} />
          )}
        </div>
      </>
    ) : null;
  },
);

// ID of secton using this renderer
ServiceManagementRenderer.ID = 'SERVICE_MANAGEMENT';
ServiceManagementRenderer.NAME = 'Service Management Team';
ServiceManagementRenderer.TYPES = ['Service'];

// Define fields managed by this section so we can know which fields to reset
// when the section is reset. Can store additional metadata for fields to use in
// the renderer above.
ServiceManagementRenderer.FIELDS = {
  'Service Owners': {},
  'Business Owners': {},
  'Technical Owners': {},
};

export default ServiceManagementRenderer;
